import {
  ApiClientError,
  apiBase,
  getToken,
  isAccessTokenExpiringSoon,
  refreshPlatformSession,
  type ApiError,
  type Session,
} from "./api";
import { beginLoading } from "./loading-activity";
import type { TurnstilePublicInfo } from "./turnstile";

type Envelope<T> = {
  data: T;
  error: ApiError | null;
  meta: { request_id: string };
};

export type HostedBranding = {
  primary_color: string;
  accent_color: string;
  background_color: string;
  text_color: string;
  display_mode: string;
};

export type HostedPortalContext = {
  realm: "platform" | "project";
  transaction: string;
  csrf_token: string;
  continuation?: string;
  expires_at: string;
  authenticated?: boolean;
  project_id?: string;
  project_slug?: string;
  project_name?: string;
  application_id?: string;
  application_name?: string;
  application_logo?: string;
  application_description?: string;
  developer_name?: string;
  developer_url?: string;
  homepage_url?: string;
  privacy_policy_url?: string;
  terms_url?: string;
  support_contact?: string;
  branding?: HostedBranding;
  eVerify?: boolean;
  scopes?: string[];
  redirect_uri?: string;
  signed_in_user?: {
    id: string;
    email?: string | null;
    username?: string | null;
  };
  previous_grant?: {
    scopes: string[];
    first_authorized_at: string;
    last_authorized_at: string;
    last_denied_at?: string | null;
    last_activity_at: string;
    authorization_count: number;
    revoked_at?: string | null;
  };
  /** Browser-safe Turnstile widget config from the API (sitekey only). */
  turnstile?: TurnstilePublicInfo;
};

export type AuthorizationDecisionResult = { redirect_to: string };

export type HostedAuthResult = {
  realm: "platform" | "project";
  continuation: string;
  user_id: string;
  /** Present for platform realm so cross-origin dashboards can store Bearer tokens. */
  session?: Session;
};

export type EmailActionContext = {
  realm: "platform" | "project";
  purpose: "verify_email" | "password_reset";
  project_slug?: string;
  project_name?: string;
  application_name?: string;
  application_logo?: string;
  support_contact?: string;
  expires_at: string;
  csrf_token: string;
  turnstile?: TurnstilePublicInfo;
};

export type PublicEmailResult = { status: string; message: string };

export type AccountProfile = {
  id: string;
  email?: string | null;
  email_verified: boolean;
  username?: string | null;
  display_name?: string | null;
  avatar_url?: string | null;
  status: string;
  password: boolean;
  google: boolean;
  created_at: string;
};

export type ConnectedApplication = {
  application_id: string;
  name: string;
  logo_url: string;
  developer_name: string;
  description: string;
  eVerify: boolean;
  scopes: string[];
  first_authorized_at: string;
  last_activity_at: string;
  revoked_at?: string | null;
};

export type AccountData = {
  profile: AccountProfile;
  applications: ConnectedApplication[];
  sessions: AccountSession[];
  history: SecurityEvent[];
};

export type AccountSession = {
  display_id: string;
  current: boolean;
  application?: string;
  device: string;
  network_hint?: string;
  signed_in_at: string;
  last_active_at: string;
  expires_at: string;
  revoked_at?: string | null;
};
export type SecurityEvent = {
  id: string;
  event_type: string;
  outcome: string;
  application?: string;
  device?: string;
  network_hint?: string;
  occurred_at: string;
};

async function portalRequest<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const endLoading = beginLoading();
  try {
    if (isAccessTokenExpiringSoon()) {
      await refreshPlatformSession();
    }
    const headers = new Headers(init.headers);
    if (init.body && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    // Dashboard Auth v2 keeps the owner session as a Bearer token. Portal
    // cookies are only set by /portal/platform/signin, so MCP consent must
    // send the stored access token or a signed-in owner looks anonymous.
    const access = getToken();
    if (access && !headers.has("Authorization")) {
      headers.set("Authorization", `Bearer ${access}`);
    }
    const response = await fetch(`${apiBase()}${path}`, {
      ...init,
      credentials: "include",
      headers,
    });
    const envelope = (await response.json()) as Envelope<T>;
    if (!response.ok || envelope.error) {
      throw new ApiClientError(
        envelope.error ?? {
          code: "HTTP_ERROR",
          message: `Request failed (${response.status})`,
        },
      );
    }
    return envelope.data;
  } finally {
    endLoading();
  }
}

/** Dedupes concurrent context creates/resumes (e.g. React StrictMode remount). */
const platformContextInflight = new Map<string, Promise<HostedPortalContext>>();

/**
 * Create or resume a platform portal transaction.
 * When `transaction` is set, the server rebinds CSRF to that handle instead of minting a new one.
 */
export function createPlatformPortalContext(
  continuation = "/dashboard/projects",
  transaction = "",
) {
  const trimmedTxn = transaction.trim();
  const key = trimmedTxn
    ? `transaction:${trimmedTxn}`
    : `continue:${continuation}`;
  const existing = platformContextInflight.get(key);
  if (existing) return existing;

  const params = new URLSearchParams();
  params.set("continue", continuation);
  if (trimmedTxn) params.set("transaction", trimmedTxn);

  const promise = portalRequest<HostedPortalContext>(
    `/portal/platform/context?${params.toString()}`,
  ).finally(() => {
    if (platformContextInflight.get(key) === promise) {
      platformContextInflight.delete(key);
    }
  });
  platformContextInflight.set(key, promise);
  return promise;
}

export function getProjectPortalContext(
  projectSlug: string,
  transaction: string,
) {
  return portalRequest<HostedPortalContext>(
    `/portal/projects/${encodeURIComponent(projectSlug)}/context?transaction=${encodeURIComponent(transaction)}`,
  );
}

export function authenticateHostedPortal(
  context: HostedPortalContext,
  action: "signin" | "signup",
  credentials: {
    email?: string;
    username?: string;
    password: string;
    turnstile_token?: string;
  },
) {
  const realmPath =
    context.realm === "platform"
      ? "/portal/platform"
      : `/portal/projects/${encodeURIComponent(context.project_slug ?? "")}`;
  return portalRequest<HostedAuthResult>(`${realmPath}/${action}`, {
    method: "POST",
    headers: { "X-CSRF-Token": context.csrf_token },
    body: JSON.stringify({ transaction: context.transaction, ...credentials }),
  });
}

export function decideHostedAuthorization(
  context: HostedPortalContext,
  decision: "confirm" | "deny",
) {
  return portalRequest<AuthorizationDecisionResult>(
    `/oauth/authorize/${decision}`,
    {
      method: "POST",
      headers: { "X-CSRF-Token": context.csrf_token },
      body: JSON.stringify({ transaction: context.transaction }),
    },
  );
}

/** MCP agent OAuth consent bootstrap (US-044 / IN-011). */
export type MCPAuthorizationContext = {
  variant: "mcp_agent";
  transaction: string;
  client_id: string;
  client_name: string;
  redirect_uri: string;
  resource: string;
  scopes: string[];
  scope_details: { scope: string; description: string }[];
  expires_at: string;
  consent_required: boolean;
  authenticated: boolean;
  signed_in_user?: { id: string; email?: string } | null;
};

export type MCPOwnedProject = {
  id: string;
  name: string;
  slug: string;
  status: string;
};

/** Load pending MCP authorization for the interactive consent page. */
export function getMCPAuthorizationContext(transaction: string) {
  return portalRequest<MCPAuthorizationContext>(
    `/oauth/mcp/transaction?transaction=${encodeURIComponent(transaction)}`,
  );
}

/**
 * Confirm or deny MCP agent access. Requires platform owner session cookie,
 * an explicit project_id, and an explicit all_projects approval when that
 * elevated scope was requested.
 */
export function decideMCPAuthorization(
  transaction: string,
  decision: "confirm" | "deny",
  projectId?: string,
  allProjects = false,
) {
  const body: {
    transaction: string;
    project_id?: string;
    all_projects?: boolean;
  } = { transaction };
  if (decision === "confirm" && projectId) {
    body.project_id = projectId;
  }
  if (decision === "confirm" && allProjects) {
    body.all_projects = true;
  }
  return portalRequest<AuthorizationDecisionResult>(
    `/oauth/authorize/${decision}`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
}

/** List projects owned by the signed-in platform owner (cookie session). */
export async function listOwnedProjectsForMCP(): Promise<MCPOwnedProject[]> {
  const endLoading = beginLoading();
  try {
    const headers = new Headers({ Accept: "application/json" });
    const access = getToken();
    if (access) headers.set("Authorization", `Bearer ${access}`);
    const response = await fetch(`${apiBase()}/v1/platform/projects`, {
      credentials: "include",
      headers,
    });
    const envelope = (await response.json()) as Envelope<MCPOwnedProject[]>;
    if (!response.ok || envelope.error) {
      throw new ApiClientError(
        envelope.error ?? {
          code: "HTTP_ERROR",
          message: `Request failed (${response.status})`,
        },
      );
    }
    return envelope.data ?? [];
  } finally {
    endLoading();
  }
}

export function changeHostedAccount(context: HostedPortalContext) {
  return portalRequest<{ status: string }>(
    `/portal/projects/${encodeURIComponent(context.project_slug ?? "")}/change-account`,
    {
      method: "POST",
      headers: { "X-CSRF-Token": context.csrf_token },
      body: JSON.stringify({ transaction: context.transaction }),
    },
  );
}

export function startGoogleProvider(
  context: HostedPortalContext,
  mode: "signin" | "link" = "signin",
  currentPassword = "",
) {
  return portalRequest<{ authorization_url: string }>(
    `/portal/providers/google/projects/${encodeURIComponent(context.project_slug ?? "")}/start`,
    {
      method: "POST",
      headers: { "X-CSRF-Token": context.csrf_token },
      body: JSON.stringify({
        transaction: context.transaction,
        mode,
        current_password: currentPassword,
      }),
    },
  );
}

function accountPath(context: HostedPortalContext) {
  return context.realm === "platform"
    ? "/portal/account/platform"
    : `/portal/account/projects/${encodeURIComponent(context.project_slug ?? "")}`;
}

export function getHostedAccount(context: HostedPortalContext) {
  return portalRequest<AccountData>(
    `${accountPath(context)}?transaction=${encodeURIComponent(context.transaction)}`,
  );
}

export function updateHostedAccount(
  context: HostedPortalContext,
  input: Partial<AccountProfile> & { current_password?: string },
) {
  return portalRequest<AccountProfile>(accountPath(context), {
    method: "PATCH",
    headers: { "X-CSRF-Token": context.csrf_token },
    body: JSON.stringify({ transaction: context.transaction, ...input }),
  });
}

/** Project Account Center only — changes app-user password and signs out portal cookies. */
export function changeHostedAccountPassword(
  context: HostedPortalContext,
  currentPassword: string,
  newPassword: string,
) {
  if (context.realm !== "project") {
    return Promise.reject(
      new ApiClientError({
        code: "INVALID_REQUEST",
        message: "password change for platform owners uses dashboard auth settings",
      }),
    );
  }
  return portalRequest<{ status: string }>(
    `${accountPath(context)}/change-password`,
    {
      method: "POST",
      headers: { "X-CSRF-Token": context.csrf_token },
      body: JSON.stringify({
        transaction: context.transaction,
        current_password: currentPassword,
        new_password: newPassword,
      }),
    },
  );
}

export function revokeConnectedApplication(
  context: HostedPortalContext,
  applicationId: string,
) {
  return portalRequest<{ status: string }>(
    `${accountPath(context)}/applications/${encodeURIComponent(applicationId)}/revoke`,
    {
      method: "POST",
      headers: { "X-CSRF-Token": context.csrf_token },
      body: JSON.stringify({ transaction: context.transaction }),
    },
  );
}

export function unlinkGoogleProvider(
  context: HostedPortalContext,
  currentPassword: string,
) {
  return portalRequest<{ status: string }>(
    `/portal/providers/google/projects/${encodeURIComponent(context.project_slug ?? "")}/unlink`,
    {
      method: "POST",
      headers: { "X-CSRF-Token": context.csrf_token },
      body: JSON.stringify({
        transaction: context.transaction,
        current_password: currentPassword,
      }),
    },
  );
}

export function revokeAccountSessions(
  context: HostedPortalContext,
  mode: "one" | "others" | "all",
  displayId = "",
) {
  return portalRequest<{ status: string }>(
    `${accountPath(context)}/sessions/revoke`,
    {
      method: "POST",
      headers: { "X-CSRF-Token": context.csrf_token },
      body: JSON.stringify({
        transaction: context.transaction,
        mode,
        display_id: displayId,
      }),
    },
  );
}

function emailRealmPath(context: HostedPortalContext | EmailActionContext) {
  return context.realm === "platform"
    ? "/portal/email/platform"
    : `/portal/email/projects/${encodeURIComponent(context.project_slug ?? "")}`;
}

export function getEmailActionContext(
  realm: "platform" | "project",
  projectSlug: string | undefined,
  action: "verify-email" | "reset-password",
  token: string,
) {
  const realmPath =
    realm === "platform"
      ? "/portal/email/platform"
      : `/portal/email/projects/${encodeURIComponent(projectSlug ?? "")}`;
  return portalRequest<EmailActionContext>(`${realmPath}/${action}/context`, {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}

export function requestHostedPasswordReset(
  context: HostedPortalContext,
  email: string,
  turnstileToken?: string,
) {
  return portalRequest<PublicEmailResult>(`${emailRealmPath(context)}/forgot`, {
    method: "POST",
    headers: { "X-CSRF-Token": context.csrf_token },
    body: JSON.stringify({
      transaction: context.transaction,
      email,
      turnstile_token: turnstileToken || undefined,
    }),
  });
}

export function resendHostedVerification(
  context: HostedPortalContext,
  turnstileToken?: string,
) {
  return portalRequest<PublicEmailResult>(`${emailRealmPath(context)}/resend`, {
    method: "POST",
    headers: { "X-CSRF-Token": context.csrf_token },
    body: JSON.stringify({
      transaction: context.transaction,
      turnstile_token: turnstileToken || undefined,
    }),
  });
}

export function completeHostedEmailAction(
  context: EmailActionContext,
  token: string,
  newPassword?: string,
  turnstileToken?: string,
) {
  const action = context.purpose === "verify_email" ? "verify" : "reset";
  return portalRequest<{ status: string }>(
    `${emailRealmPath(context)}/${action}`,
    {
      method: "POST",
      headers: { "X-CSRF-Token": context.csrf_token },
      body: JSON.stringify({
        token,
        new_password: newPassword ?? "",
        turnstile_token: turnstileToken || undefined,
      }),
    },
  );
}

export function safeBrandColor(
  value: string | undefined,
  fallback: string,
): string {
  return value && /^#[0-9a-fA-F]{6}$/.test(value) ? value : fallback;
}

export function safeHostedImageURL(value: string | undefined): string | null {
  if (!value) return null;
  try {
    const parsed = new URL(value);
    return parsed.protocol === "https:" || parsed.protocol === "http:"
      ? parsed.toString()
      : null;
  } catch {
    return null;
  }
}

export function safeHostedOrigin(value: string | undefined): string {
  const safe = safeHostedImageURL(value);
  return safe ? new URL(safe).origin : "Registered application callback";
}
